import React, { useState, useEffect } from 'react';
import { CourtPOI, SportType, PickupGame } from '../types';
import { X, Flame, MapPin, Calendar, Users, Sparkles, Mail, MessageSquare, Copy, Check, Send, Link, Save, RefreshCw } from 'lucide-react';

interface HostGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  courts: CourtPOI[];
  initialCourt?: CourtPOI;
  onHostGame: (gameData: Partial<PickupGame>) => void;
}

const HOST_DRAFT_KEY = 'host_game_modal_draft';

export const HostGameModal: React.FC<HostGameModalProps> = ({
  isOpen,
  onClose,
  courts,
  initialCourt,
  onHostGame,
}) => {
  const [title, setTitle] = useState('5v5 Friday Playground Run');
  const [sport, setSport] = useState<SportType>(initialCourt ? initialCourt.sport : 'basketball');
  const [selectedCourtId, setSelectedCourtId] = useState<string>(initialCourt ? initialCourt.id : courts[0]?.id || '1');
  const [addressInput, setAddressInput] = useState<string>(initialCourt ? initialCourt.address : courts[0]?.address || '155th St & Frederick Douglass Blvd, New York, NY');
  const [showAddressSuggestions, setShowAddressSuggestions] = useState<boolean>(false);
  const [dateTime, setDateTime] = useState('Tomorrow • 6:30 PM');
  const [maxPlayers, setMaxPlayers] = useState(10);
  const [level, setLevel] = useState<'Casual' | 'Competitive' | 'Varsity / Intramural'>('Competitive');
  const [description, setDescription] = useState('Winners stay on court. Bring light and dark jerseys.');
  const [draftRestored, setDraftRestored] = useState(false);

  // Invites state (Email & SMS text messages)
  const [inviteEmails, setInviteEmails] = useState<string[]>([
    'alex@playgrounds.app',
    'jordan@school.edu',
    'marcus.vance@varsity.org',
    'maya.lin@selectball.com',
  ]);
  const [invitePhones, setInvitePhones] = useState<string[]>([
    '+1 (555) 234-5678',
    '+1 (555) 987-6543',
    '+1 (555) 456-7890',
  ]);

  const [newEmailInput, setNewEmailInput] = useState('');
  const [newPhoneInput, setNewPhoneInput] = useState('');

  const [sendEmailInvites, setSendEmailInvites] = useState(true);
  const [sendSmsInvites, setSendSmsInvites] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  // Restore draft on modal open if present in localStorage
  useEffect(() => {
    if (!isOpen) return;
    const saved = localStorage.getItem(HOST_DRAFT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.title) setTitle(parsed.title);
        if (parsed.sport) setSport(parsed.sport);
        if (parsed.selectedCourtId) setSelectedCourtId(parsed.selectedCourtId);
        if (parsed.addressInput) setAddressInput(parsed.addressInput);
        if (parsed.dateTime) setDateTime(parsed.dateTime);
        if (parsed.maxPlayers) setMaxPlayers(parsed.maxPlayers);
        if (parsed.level) setLevel(parsed.level);
        if (parsed.description) setDescription(parsed.description);
        if (parsed.inviteEmails) setInviteEmails(parsed.inviteEmails);
        if (parsed.invitePhones) setInvitePhones(parsed.invitePhones);
        setDraftRestored(true);
        setTimeout(() => setDraftRestored(false), 3000);
      } catch (err) {
        console.error('Failed to parse host game draft:', err);
      }
    }
  }, [isOpen]);

  // Periodically save draft to localStorage on input change
  useEffect(() => {
    if (!isOpen) return;
    const draftData = {
      title,
      sport,
      selectedCourtId,
      addressInput,
      dateTime,
      maxPlayers,
      level,
      description,
      inviteEmails,
      invitePhones,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(HOST_DRAFT_KEY, JSON.stringify(draftData));
  }, [isOpen, title, sport, selectedCourtId, addressInput, dateTime, maxPlayers, level, description, inviteEmails, invitePhones]);

  const handleClearDraft = () => {
    localStorage.removeItem(HOST_DRAFT_KEY);
    setTitle('5v5 Friday Playground Run');
    setDescription('Winners stay on court. Bring light and dark jerseys.');
    setDateTime('Tomorrow • 6:30 PM');
    setMaxPlayers(10);
    setDraftRestored(false);
  };

  // Preset friends & teammates to quick-add to multi-invite list
  const suggestedTeammates = [
    { name: 'Jordan Bell', email: 'jordan.bell@highschool.edu', phone: '+1 (555) 111-2222', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200' },
    { name: 'Marcus Vance', email: 'marcus.vance@varsity.org', phone: '+1 (555) 333-4444', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200' },
    { name: 'Maya Lin', email: 'maya.lin@selectball.com', phone: '+1 (555) 555-6666', avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=200' },
    { name: 'Tariq Simmons', email: 'tariq.s@streetball.org', phone: '+1 (555) 777-8888', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200' },
    { name: 'Devon Davis', email: 'devon.d@columbia.edu', phone: '+1 (555) 999-0000', avatar: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?auto=format&fit=crop&q=80&w=200' },
    { name: 'Elena Rostova', email: 'elena.r@soccerpro.org', phone: '+1 (555) 222-3333', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=200' },
  ];

  if (!isOpen) return null;

  const court = courts.find((c) => c.id === selectedCourtId) || courts[0];
  const generateInviteCode = () => `RUN_${title.replace(/\s+/g, '').substring(0, 6).toUpperCase()}_${Date.now().toString().slice(-4)}`;
  const sampleInviteLink = `https://playgrounds.app/join?game=${title.replace(/\s+/g, '_').toLowerCase()}&code=INVITE2026`;

  const handleAddEmail = () => {
    if (!newEmailInput.trim()) return;
    const added = newEmailInput.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
    setInviteEmails((prev) => Array.from(new Set([...prev, ...added])));
    setNewEmailInput('');
  };

  const handleRemoveEmail = (email: string) => {
    setInviteEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleAddPhone = () => {
    if (!newPhoneInput.trim()) return;
    const added = newPhoneInput.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
    setInvitePhones((prev) => Array.from(new Set([...prev, ...added])));
    setNewPhoneInput('');
  };

  const handleRemovePhone = (phone: string) => {
    setInvitePhones((prev) => prev.filter((p) => p !== phone));
  };

  const toggleTeammate = (t: typeof suggestedTeammates[0]) => {
    if (inviteEmails.includes(t.email)) {
      setInviteEmails((prev) => prev.filter((e) => e !== t.email));
      setInvitePhones((prev) => prev.filter((p) => p !== t.phone));
    } else {
      setInviteEmails((prev) => Array.from(new Set([...prev, t.email])));
      setInvitePhones((prev) => Array.from(new Set([...prev, t.phone])));
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(sampleInviteLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const emailsList = sendEmailInvites ? inviteEmails : [];
    const phonesList = sendSmsInvites ? invitePhones : [];

    localStorage.removeItem(HOST_DRAFT_KEY);

    onHostGame({
      title,
      sport,
      courtId: court.id,
      courtName: court.name,
      address: addressInput || court.address,
      dateTime,
      maxPlayers,
      level,
      description,
      invitedEmails: emailsList,
      invitedPhones: phonesList,
      inviteCode: generateInviteCode(),
      inviteUrl: sampleInviteLink,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-indigo-900/95 text-white rounded-[2.5rem] border border-white/15 shadow-2xl w-full max-w-xl overflow-hidden space-y-5 p-6 my-6 relative">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {draftRestored && (
          <div className="bg-lime-400/20 border border-lime-400/40 text-lime-300 px-3.5 py-2 rounded-xl text-xs font-bold flex items-center justify-between">
            <span className="flex items-center space-x-1.5">
              <Check className="w-4 h-4 text-lime-400" />
              <span>Draft restored from your last session</span>
            </span>
            <button
              onClick={handleClearDraft}
              className="text-[10px] text-lime-400 underline font-mono hover:text-white"
            >
              Reset Form
            </button>
          </div>
        )}

        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Flame className="w-6 h-6 text-lime-400" />
            <h2 className="text-xl font-black italic uppercase text-white">
              Host a Pickup Game & Invite Players
            </h2>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold">
            Organize local playground runs and automatically send email & text message invites with direct registration links.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs font-semibold max-h-[75vh] overflow-y-auto pr-1">
          {/* Title */}
          <div>
            <label className="text-indigo-200/90 block mb-1 font-bold">Game Title *</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
            />
          </div>

          {/* Sport & Level */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-indigo-200/90 block mb-1 font-bold">Sport</label>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value as SportType)}
                className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="basketball">Basketball 🏀</option>
                <option value="baseball">Baseball ⚾</option>
                <option value="softball">Softball 🥎</option>
                <option value="pickleball">Pickleball 🏓</option>
                <option value="soccer">Soccer ⚽</option>
                <option value="volleyball">Volleyball 🏐</option>
                <option value="football">Football 🏈</option>
                <option value="tennis">Tennis 🎾</option>
              </select>
            </div>

            <div>
              <label className="text-indigo-200/90 block mb-1 font-bold">Competitive Level</label>
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value as any)}
                className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="Casual">Casual</option>
                <option value="Competitive">Competitive</option>
                <option value="Varsity / Intramural">Varsity / Intramural</option>
              </select>
            </div>
          </div>

          {/* Court Selection */}
          <div>
            <label className="text-indigo-200/90 block mb-1 font-bold">Select Court / Park Venue</label>
            <select
              value={selectedCourtId}
              onChange={(e) => {
                const cId = e.target.value;
                setSelectedCourtId(cId);
                const matched = courts.find((c) => c.id === cId);
                if (matched) {
                  setAddressInput(matched.address);
                }
              }}
              className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
            >
              {courts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.address})
                </option>
              ))}
            </select>
          </div>

          {/* Address Field with Auto-Suggestion Dropdown */}
          <div className="relative">
            <div className="flex items-center justify-between mb-1">
              <label className="text-indigo-200/90 block font-bold flex items-center">
                <MapPin className="w-3.5 h-3.5 text-lime-400 mr-1" />
                Address / Location *
              </label>
              <span className="text-[10px] text-lime-300 font-extrabold uppercase bg-lime-400/20 px-2 py-0.5 rounded-full border border-lime-400/30">
                ⚡ Auto-Suggest On
              </span>
            </div>
            <input
              type="text"
              required
              value={addressInput}
              onFocus={() => setShowAddressSuggestions(true)}
              onChange={(e) => {
                setAddressInput(e.target.value);
                setShowAddressSuggestions(true);
              }}
              placeholder="Type or select a popular court address..."
              className="w-full p-2.5 bg-indigo-950 text-white placeholder-indigo-300/40 rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 font-medium"
            />

            {/* Auto-Suggestion Dropdown */}
            {showAddressSuggestions && (
              <div className="absolute left-0 right-0 top-full mt-1.5 bg-indigo-950 text-white rounded-2xl border border-lime-400/40 shadow-2xl z-50 max-h-56 overflow-y-auto p-1.5 space-y-1">
                <div className="px-2.5 py-1 text-[10px] font-black uppercase text-lime-400 border-b border-white/10 flex items-center justify-between">
                  <span>📍 Popular Local Courts & Venues</span>
                  <button
                    type="button"
                    onClick={() => setShowAddressSuggestions(false)}
                    className="text-indigo-300 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                {(() => {
                  const predefined = [
                    { name: 'Holcombe Rucker Park', address: '155th St & Frederick Douglass Blvd, New York, NY', sport: 'basketball' },
                    { name: 'Venice Beach Basketball Courts', address: '1800 Ocean Front Walk, Venice, CA', sport: 'basketball' },
                    { name: 'West 4th Street Courts (The Cage)', address: '3 6th Ave, New York, NY', sport: 'basketball' },
                    { name: 'Dyckman Park Grounds', address: '204th St & Nagle Ave, New York, NY', sport: 'basketball' },
                    { name: 'Mosswood Recreation Center', address: '3612 Webster St, Oakland, CA', sport: 'basketball' },
                    { name: 'Pan Pacific Park Courts', address: '7600 Beverly Blvd, Los Angeles, CA', sport: 'basketball' },
                    { name: 'Green Lake Park Courts', address: '7201 E Green Lake Dr N, Seattle, WA', sport: 'pickleball' },
                    { name: 'Lincoln Park Turf', address: '500 N Cannon Dr, Chicago, IL', sport: 'soccer' },
                    { name: 'Glover Park Rec Center', address: '4200 Wisconsin Ave NW, Washington, DC', sport: 'baseball' },
                  ];

                  const allSuggestions = [
                    ...courts.map((c) => ({ name: c.name, address: c.address, sport: c.sport, courtId: c.id })),
                    ...predefined,
                  ];

                  const filtered = allSuggestions.filter(
                    (s, index, self) =>
                      index === self.findIndex((t) => t.address.toLowerCase() === s.address.toLowerCase()) &&
                      (s.name.toLowerCase().includes(addressInput.toLowerCase()) ||
                        s.address.toLowerCase().includes(addressInput.toLowerCase()) ||
                        s.sport.toLowerCase().includes(addressInput.toLowerCase()))
                  );

                  if (filtered.length === 0) {
                    return (
                      <div className="p-3 text-center text-xs text-indigo-300/70 font-medium">
                        Custom address: "{addressInput}"
                      </div>
                    );
                  }

                  return filtered.map((sug) => (
                    <button
                      key={sug.address + sug.name}
                      type="button"
                      onClick={() => {
                        setAddressInput(sug.address);
                        if ('courtId' in sug && sug.courtId) {
                          setSelectedCourtId(String(sug.courtId));
                        }
                        setShowAddressSuggestions(false);
                      }}
                      className="w-full text-left p-2 rounded-xl hover:bg-lime-400 hover:text-black transition flex items-center justify-between group text-xs"
                    >
                      <div className="min-w-0 pr-2">
                        <p className="font-extrabold truncate group-hover:text-black">{sug.name}</p>
                        <p className="text-[10px] text-indigo-300 group-hover:text-black/80 truncate">{sug.address}</p>
                      </div>
                      <span className="shrink-0 px-2 py-0.5 rounded-md bg-indigo-900 group-hover:bg-black group-hover:text-lime-400 text-[9px] font-black uppercase text-lime-300">
                        {sug.sport}
                      </span>
                    </button>
                  ));
                })()}
              </div>
            )}
          </div>

          {/* Date & Capacity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-indigo-200/90 block mb-1 font-bold">Date & Time</label>
              <input
                type="text"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div>
              <label className="text-indigo-200/90 block mb-1 font-bold">Max Player Capacity</label>
              <input
                type="number"
                min={2}
                max={30}
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(Number(e.target.value))}
                className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
          </div>

          {/* Rules / Description */}
          <div>
            <label className="text-indigo-200/90 block mb-1 font-bold">Game Notes & Rules</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2.5 bg-indigo-950 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
            />
          </div>

          {/* AUTOMATED MULTI-EMAIL & MULTI-SMS INVITE SECTION */}
          <div className="bg-indigo-950/80 p-4 rounded-2xl border border-lime-400/30 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-black italic uppercase text-lime-400 text-xs flex items-center">
                <Send className="w-4 h-4 mr-1.5 text-lime-400" />
                Invite Multiple Players (Email & Text Messages)
              </span>
              <span className="text-[10px] bg-lime-400/20 text-lime-300 font-black px-2 py-0.5 rounded-full border border-lime-400/30">
                {inviteEmails.length} Emails • {invitePhones.length} SMS
              </span>
            </div>

            {/* Quick Teammate / Friend Selector */}
            <div className="space-y-1.5">
              <label className="text-indigo-200 block text-[11px] font-bold">
                ⚡ Quick Select Teammates & Friends:
              </label>
              <div className="flex flex-wrap gap-1.5">
                {suggestedTeammates.map((t) => {
                  const isSelected = inviteEmails.includes(t.email);
                  return (
                    <button
                      key={t.email}
                      type="button"
                      onClick={() => toggleTeammate(t)}
                      className={`px-2.5 py-1 rounded-xl text-[10px] font-extrabold flex items-center space-x-1.5 transition ${
                        isSelected
                          ? 'bg-lime-400 text-black shadow-md'
                          : 'bg-indigo-900/90 text-indigo-200 border border-white/10 hover:text-white'
                      }`}
                    >
                      <img src={t.avatar} alt={t.name} className="w-3.5 h-3.5 rounded-full object-cover" />
                      <span>{t.name}</span>
                      {isSelected ? <Check className="w-3 h-3 text-black" /> : <Users className="w-3 h-3 text-indigo-300/60" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email Invites Input & Tags */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-indigo-200 flex items-center font-bold text-xs">
                  <Mail className="w-3.5 h-3.5 text-lime-400 mr-1" />
                  Email Addresses ({inviteEmails.length})
                </label>
                <label className="flex items-center space-x-1 cursor-pointer text-[10px] text-indigo-300 font-semibold">
                  <input
                    type="checkbox"
                    checked={sendEmailInvites}
                    onChange={(e) => setSendEmailInvites(e.target.checked)}
                    className="accent-lime-400 rounded"
                  />
                  <span>Send Emails</span>
                </label>
              </div>

              {/* Tag Pills */}
              {inviteEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2 bg-indigo-900/40 rounded-xl border border-white/5">
                  {inviteEmails.map((email) => (
                    <span
                      key={email}
                      className="px-2.5 py-1 bg-indigo-800 text-lime-300 font-mono text-[10px] rounded-lg border border-lime-400/30 flex items-center space-x-1"
                    >
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="text-indigo-300 hover:text-white ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Add another email (or comma separated)..."
                  value={newEmailInput}
                  onChange={(e) => setNewEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  disabled={!sendEmailInvites}
                  className="flex-1 p-2 bg-indigo-900/90 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 text-xs disabled:opacity-40"
                />
                <button
                  type="button"
                  onClick={handleAddEmail}
                  disabled={!sendEmailInvites}
                  className="px-3 py-2 bg-indigo-800 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl border border-white/10 transition disabled:opacity-40"
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Text Message / SMS Invites Input & Tags */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-indigo-200 flex items-center font-bold text-xs">
                  <MessageSquare className="w-3.5 h-3.5 text-rose-400 mr-1" />
                  SMS Text Numbers ({invitePhones.length})
                </label>
                <label className="flex items-center space-x-1 cursor-pointer text-[10px] text-indigo-300 font-semibold">
                  <input
                    type="checkbox"
                    checked={sendSmsInvites}
                    onChange={(e) => setSendSmsInvites(e.target.checked)}
                    className="accent-lime-400 rounded"
                  />
                  <span>Send Text Messages</span>
                </label>
              </div>

              {/* Tag Pills */}
              {invitePhones.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2 bg-indigo-900/40 rounded-xl border border-white/5">
                  {invitePhones.map((phone) => (
                    <span
                      key={phone}
                      className="px-2.5 py-1 bg-indigo-800 text-rose-300 font-mono text-[10px] rounded-lg border border-rose-400/30 flex items-center space-x-1"
                    >
                      <span>{phone}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePhone(phone)}
                        className="text-indigo-300 hover:text-white ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Add phone number (+1 555 123-4567)..."
                  value={newPhoneInput}
                  onChange={(e) => setNewPhoneInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddPhone();
                    }
                  }}
                  disabled={!sendSmsInvites}
                  className="flex-1 p-2 bg-indigo-900/90 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 text-xs disabled:opacity-40"
                />
                <button
                  type="button"
                  onClick={handleAddPhone}
                  disabled={!sendSmsInvites}
                  className="px-3 py-2 bg-indigo-800 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl border border-white/10 transition disabled:opacity-40"
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Direct Link Share & Copy */}
            <div className="pt-1 space-y-1">
              <span className="text-[10px] text-indigo-300/80 font-bold block">Shareable Direct Game & Sign-Up Link:</span>
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-2 bg-black/60 rounded-xl border border-white/10 text-[10px] font-mono text-lime-300 truncate">
                  {sampleInviteLink}
                </div>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-3 py-2 bg-indigo-800 hover:bg-indigo-700 text-white font-bold text-[10px] uppercase italic rounded-xl border border-white/10 flex items-center space-x-1 transition"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-lime-400" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedLink ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>

            {/* Message Preview */}
            <div className="p-3 bg-indigo-900/50 rounded-xl border border-white/10 text-[10px] space-y-1">
              <span className="font-extrabold text-lime-300 block uppercase">Sample Automated Invite Message:</span>
              <p className="text-indigo-200/90 italic">
                "🏀 You are invited to '{title}' at {court.name} on {dateTime}! Click link to join game or register your athlete profile: {sampleInviteLink}"
              </p>
            </div>
          </div>

          {/* Submit Actions */}
          <div className="pt-2 flex items-center justify-between">
            <span className="text-[11px] text-lime-300 font-bold italic">
              {sendEmailInvites ? inviteEmails.length : 0} Emails & {sendSmsInvites ? invitePhones.length : 0} SMS Text Invites Ready
            </span>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-indigo-200 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5"
              >
                <Sparkles className="w-4 h-4" />
                <span>Host Game & Send Invites</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

